import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { shareWhatsApp } from '../utils/whatsapp';
import { useAuth } from '../utils/AuthContext';
import { useLang } from '../utils/LanguageContext';
import { t } from '../utils/translations';
import './SettingsScreen.css';
import Loader from '../components/Loader';

const ROLES = ['owner', 'manager', 'cashier'];

function SettingsScreen() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [savingCats, setSavingCats] = useState(false);
  const [name, setName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [accountMsg, setAccountMsg] = useState('');
  const [savingAccount, setSavingAccount] = useState(false);
  const [newBranch, setNewBranch] = useState('');
  const [branchMsg, setBranchMsg] = useState('');
  const [savingBranch, setSavingBranch] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { org, role, user, profile, isMax, branches, switchBranch, refreshProfile } = useAuth();
  const { lang } = useLang();
  const tr = t[lang];

  useEffect(() => {
    if (profile?.full_name) setName(profile.full_name);
  }, [profile]);

  const categories = org?.categories || [];

  const roleLabel = {
    owner: tr.roleOwner,
    manager: tr.roleManager,
    cashier: tr.roleCashier,
  };

  useEffect(() => { fetchMembers(); }, []);

  async function fetchMembers() {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at');
    setMembers(data || []);
    setLoading(false);
  }

  async function changeRole(memberId, newRole) {
    // Role changes go through a guarded server function (owner-only, same shop)
    await supabase.rpc('set_member_role', { target: memberId, new_role: newRole });
    fetchMembers();
  }

  async function changeBranchType(newType) {
    if (newType === (org?.type || 'cafe')) return;
    await supabase.from('organizations').update({ type: newType }).eq('id', org.id);
    // Type reshapes the whole app (POS vs Réservations) → reload cleanly
    window.location.reload();
  }

  async function removeMember(memberId, name) {
    // Owner-only, guarded server function; detaches the member from the café.
    if (!window.confirm(tr.removeConfirm.replace('{name}', name))) return;
    const { error } = await supabase.rpc('remove_member', { target: memberId });
    if (error) {
      window.alert(tr.removeError);
    } else {
      fetchMembers();
    }
  }

  async function exportData() {
    setExporting(true);
    const [prod, ords, custs, pays] = await Promise.all([
      supabase.from('products').select('*'),
      supabase.from('orders').select('*, order_items(*)').order('created_at'),
      supabase.from('customers').select('*'),
      supabase.from('carnet_payments').select('*'),
    ]);
    const backup = {
      exported_at: new Date().toISOString(),
      shop: org?.name,
      products: prod.data || [],
      orders: ords.data || [],
      customers: custs.data || [],
      carnet_payments: pays.data || [],
    };
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' })
    );
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().slice(0, 10);
    a.download = `stratospos-backup-${(org?.name || 'shop').replace(/\s+/g, '-')}-${date}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setExporting(false);
  }

  function copyCode() {
    navigator.clipboard?.writeText(org?.join_code || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function saveCategories(next) {
    setSavingCats(true);
    await supabase.from('organizations').update({ categories: next }).eq('id', org.id);
    await refreshProfile();
    setSavingCats(false);
  }

  async function addCategory(e) {
    e.preventDefault();
    const name = newCat.trim();
    if (!name || categories.includes(name)) return;
    await saveCategories([...categories, name]);
    setNewCat('');
  }

  async function removeCategory(cat) {
    if (categories.length <= 1) return;
    await saveCategories(categories.filter(c => c !== cat));
  }

  async function createBranch(e) {
    e.preventDefault();
    const nm = newBranch.trim();
    if (!nm) return;
    setSavingBranch(true);
    setBranchMsg('');
    const { error } = await supabase.rpc('create_branch', { branch_name: nm });
    if (error) {
      const msg = (error.message || '');
      setBranchMsg(
        msg.includes('BRANCH_LIMIT') ? tr.branchLimit :
        msg.includes('MAX_ONLY') ? tr.proOnly :
        tr.signUpError
      );
    } else {
      setNewBranch('');
      setBranchMsg(tr.branchCreated);
      await refreshProfile();
      setTimeout(() => setBranchMsg(''), 4000);
    }
    setSavingBranch(false);
  }

  async function saveAccount(e) {
    e.preventDefault();
    setAccountMsg('');

    if (newPassword && newPassword.length < 8) {
      setAccountMsg(tr.passwordShort);
      return;
    }

    setSavingAccount(true);

    // Update display name (shown in the team list) if it changed
    const trimmed = name.trim();
    if (trimmed && trimmed !== profile?.full_name) {
      await supabase.from('profiles').update({ full_name: trimmed }).eq('id', user.id);
    }

    // Update password if a new one was typed
    if (newPassword) {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        setAccountMsg(tr.resetError);
        setSavingAccount(false);
        return;
      }
    }

    await refreshProfile();
    fetchMembers();
    setNewPassword('');
    setAccountMsg(tr.accountSaved);
    setSavingAccount(false);
    setTimeout(() => setAccountMsg(''), 3000);
  }

  if (loading) return <Loader text={tr.loadingApp} />;

  return (
    <div className="settings-screen">
      <h2 className="settings-title">⚙️ {tr.settings}</h2>

      <div className="settings-card">
        <h3>{tr.myAccount}</h3>
        <form className="account-form" onSubmit={saveAccount}>
          <div className="settings-row">
            <span className="settings-label">{tr.email}</span>
            <span className="settings-value">{user?.email}</span>
          </div>
          <label className="account-label">{tr.yourName}</label>
          <input
            type="text"
            className="cat-add-input"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={tr.fullName}
          />
          <label className="account-label">{tr.newPasswordOptional}</label>
          <input
            type="password"
            className="cat-add-input"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
          />
          {accountMsg && <p className="account-msg">{accountMsg}</p>}
          <button type="submit" className="cat-add-btn" disabled={savingAccount}>
            {savingAccount ? '...' : tr.save}
          </button>
        </form>
      </div>

      <div className="settings-card">
        <h3>{tr.shopInfo}</h3>
        <div className="settings-row">
          <span className="settings-label">{tr.shopName}</span>
          <span className="settings-value">{org?.name}</span>
        </div>
        <div className="settings-row">
          <span className="settings-label">{tr.joinCode}</span>
          <div className="join-code-actions">
            <button className="join-code" onClick={copyCode}>
              {org?.join_code} {copied ? '✅' : '📋'}
            </button>
            <button
              className="wa-share-btn"
              title="WhatsApp"
              onClick={() => shareWhatsApp(
                `${tr.joinInvite.replace('{shop}', org?.name || '')}\n` +
                `https://stratospos.pages.dev\n${tr.joinCode}: ${org?.join_code}`
              )}
            >📱</button>
          </div>
        </div>
        {org?.plan && (
          <div className="settings-row">
            <span className="settings-label">{tr.planLabel}</span>
            <span className={`role-badge plan-${org.plan}`}>
              {org.plan === 'max' ? tr.planMax : org.plan === 'pro' ? tr.planPro : tr.planBasic}
            </span>
          </div>
        )}
        {org?.paid_until && (
          <div className="settings-row">
            <span className="settings-label">{tr.paidUntil}</span>
            <span className="settings-value">
              {new Date(org.paid_until).toLocaleDateString(
                lang === 'ar' ? 'ar-MA' : 'fr-MA',
                { day: '2-digit', month: 'short', year: 'numeric' }
              )}
            </span>
          </div>
        )}
        <p className="settings-hint">{tr.joinCodeHint}</p>
      </div>

      {role === 'owner' && (
        <div className="settings-card">
          <h3>🏛️ {tr.branchTypeTitle}</h3>
          <div className="type-toggle">
            <button
              className={`type-btn ${(org?.type || 'cafe') !== 'events' ? 'active' : ''}`}
              onClick={() => changeBranchType('cafe')}
            >☕ {tr.branchTypeCafe}</button>
            <button
              className={`type-btn ${org?.type === 'events' ? 'active' : ''}`}
              onClick={() => changeBranchType('events')}
            >💒 {tr.branchTypeEvents}</button>
          </div>
          <p className="settings-hint">{tr.branchTypeHint}</p>
        </div>
      )}

      {isMax && role === 'owner' && (
        <div className="settings-card">
          <h3>🏢 {tr.branchesTitle} <span className="role-badge plan-max">{tr.planMax}</span></h3>
          {branches.map(b => (
            <div key={b.id} className="settings-row">
              <span className={b.id === org?.id ? 'settings-value' : 'settings-label'}>
                {b.name} {b.id === org?.id ? '✓' : ''}
              </span>
              {b.id !== org?.id && (
                <button className="branch-goto" onClick={() => switchBranch(b.id)}>
                  {tr.goToBranch} →
                </button>
              )}
            </div>
          ))}
          <form className="cat-add-row" onSubmit={createBranch}>
            <input
              type="text"
              placeholder={tr.newBranch}
              value={newBranch}
              onChange={e => setNewBranch(e.target.value)}
              className="cat-add-input"
              maxLength={60}
            />
            <button type="submit" className="cat-add-btn" disabled={savingBranch || !newBranch.trim()}>
              {savingBranch ? '...' : tr.create}
            </button>
          </form>
          {branchMsg && <p className="account-msg">{branchMsg}</p>}
          <p className="settings-hint">{tr.branchHint}</p>
        </div>
      )}

      {role === 'owner' && (
        <div className="settings-card">
          <h3>{tr.categoriesTitle}</h3>
          <div className="cat-chips">
            {categories.map(cat => (
              <span key={cat} className="cat-chip">
                {cat}
                {categories.length > 1 && (
                  <button
                    className="cat-remove"
                    onClick={() => removeCategory(cat)}
                    disabled={savingCats}
                  >✕</button>
                )}
              </span>
            ))}
          </div>
          <form className="cat-add-row" onSubmit={addCategory}>
            <input
              type="text"
              placeholder={tr.newCategory}
              value={newCat}
              onChange={e => setNewCat(e.target.value)}
              className="cat-add-input"
              maxLength={40}
            />
            <button type="submit" className="cat-add-btn" disabled={savingCats || !newCat.trim()}>
              {tr.add}
            </button>
          </form>
          <p className="settings-hint">{tr.categoriesHint}</p>
        </div>
      )}

      <div className="settings-card">
        <h3>{tr.team}</h3>
        {members.map(m => (
          <div key={m.id} className="member-row">
            <div className="member-info">
              <span className="member-name">
                {m.full_name || m.email || m.id.slice(0, 8)}
                {m.id === user?.id ? ' (✓)' : ''}
              </span>
              {m.email && m.full_name && <span className="member-email">{m.email}</span>}
            </div>
            {role === 'owner' && m.id !== user?.id ? (
              <div className="member-actions">
                <select
                  className="role-select"
                  value={m.role}
                  onChange={e => changeRole(m.id, e.target.value)}
                >
                  {ROLES.map(r => (
                    <option key={r} value={r}>{roleLabel[r]}</option>
                  ))}
                </select>
                {m.role !== 'owner' && (
                  <button
                    className="member-remove"
                    title={tr.removeMember}
                    aria-label={tr.removeMember}
                    onClick={() => removeMember(m.id, m.full_name || m.email || m.id.slice(0, 8))}
                  >🗑️</button>
                )}
              </div>
            ) : (
              <span className={`role-badge role-${m.role}`}>{roleLabel[m.role]}</span>
            )}
          </div>
        ))}
      </div>

      {role === 'owner' && (
        <div className="settings-card">
          <h3>🔒 {tr.dataBackup}</h3>
          <p className="settings-hint" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
            {tr.dataBackupHint}
          </p>
          <button className="cat-add-btn" onClick={exportData} disabled={exporting}>
            {exporting ? '...' : `⬇️ ${tr.exportBackup}`}
          </button>
          <p className="settings-hint">
            <a href="/legal" className="legal-link">{tr.privacyPolicy}</a>
            {' · '}
            <a href="/legal#terms" className="legal-link">{tr.termsOfService}</a>
          </p>
        </div>
      )}
    </div>
  );
}

export default SettingsScreen;
